<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Dashboard</span>
            <div>
              <v-btn color="primary" @click="loadData" :loading="loading">
                <v-icon left>mdi-refresh</v-icon>
                Yenile
              </v-btn>
              <v-btn color="success" to="/servers" class="ml-2">
                <v-icon left>mdi-server</v-icon>
                Sunucular
              </v-btn>
              <v-btn color="info" to="/auto-restart" class="ml-2">
                <v-icon left>mdi-timer-cog</v-icon>
                Otomatik Restart
              </v-btn>
            </div>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="dataloggers.length === 0 && !loading">
      <v-col cols="12">
        <v-alert type="info" class="text-center">
          Henüz datalogger bulunamadı. Önce sunucu ekleyin.
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-data-table :headers="headers" :items="sortedDataloggers" :loading="loading" :items-per-page="-1"
            class="elevation-1" item-value="id" show-expand expand-on-click>
            <template v-slot:item.name="{ item }">
              <div class="d-flex align-center">
                <v-chip :color="getDataloggerStatusColor(item)" small class="mr-2">
                  {{ getDataloggerStatus(item) }}
                </v-chip>
                <span class="font-weight-medium">{{ item.name }}</span>
                <!-- Güncel mesaj ikonu - herhangi bir component'ten son 5 dakika içinde mesaj geldiyse -->
                <v-icon v-if="hasRecentMessage(item)" color="success" size="small" class="ml-2">
                  mdi-pulse
                </v-icon>
                <v-tooltip v-if="hasRecentMessage(item)" activator="parent" location="top">
                  Son 5 dakika içinde mesaj alındı
                </v-tooltip>
              </div>
            </template>

            <template v-slot:item.server="{ item }">
              <div class="d-flex align-center justify-center">
                <v-chip v-if="item.server" color="primary" small>
                  {{ item.server.name }}
                </v-chip>
                <v-chip v-else color="grey" small>
                  Bilinmiyor
                </v-chip>
              </div>
            </template>

            <template v-slot:item.groups="{ item }">
              <div class="d-flex align-center">
                <v-icon :color="getStatusColor(item.groups)" class="mr-1">
                  mdi-folder-multiple
                </v-icon>
                <span>{{ getStatusText(item.groups) }}</span>
              </div>
            </template>

            <template v-slot:item.clients="{ item }">
              <div class="d-flex align-center">
                <v-icon :color="getStatusColor(item.clients)" class="mr-1">
                  mdi-desktop-classic
                </v-icon>
                <span>{{ getStatusText(item.clients) }}</span>
              </div>
            </template>

            <template v-slot:item.history="{ item }">
              <div class="d-flex align-center">
                <v-icon :color="getStatusColor(item.history)" class="mr-1">
                  mdi-history
                </v-icon>
                <span>{{ getStatusText(item.history) }}</span>
              </div>
            </template>

            <template v-slot:item.offlineRatio="{ item }">
              <div class="d-flex align-center justify-center">
                <v-chip :color="getOfflineRatioColor(calculateOfflineRatio(item))" small>
                  %{{ Math.round(calculateOfflineRatio(item) * 100) }}
                </v-chip>
              </div>
            </template>



            <template v-slot:expanded-row="{ item }">
              <td :colspan="headers.length">
                <v-container class="pa-4">
                  <v-row>
                    <!-- Groups -->
                    <v-col cols="12" md="6" v-if="item.groups.length > 0">
                      <v-card variant="outlined">
                        <v-card-title class="text-h6">
                          <v-icon left>mdi-folder-multiple</v-icon>
                          Groups ({{ item.groups.length }})
                        </v-card-title>
                        <v-card-text>
                          <div v-for="group in item.groups" :key="group.id" class="mb-4">
                            <v-card variant="outlined" class="pa-3">
                              <div class="d-flex align-center mb-2">
                                <v-icon :color="getDataTimeBasedStatus(group.status, group.messages) === 'online' ? 'success' : 'error'" size="small"
                                  class="mr-2">
                                  {{ getDataTimeBasedStatus(group.status, group.messages) === 'online' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                </v-icon>
                                <span class="font-weight-medium">{{ group.name }} (ID: {{ group.id }})</span>
                                <!-- Güncel mesaj ikonu -->
                                <v-icon v-if="isRecentMessage(group.status.lastSeen)" color="success" size="small" class="ml-2">
                                  mdi-pulse
                                </v-icon>
                                <v-tooltip v-if="isRecentMessage(group.status.lastSeen)" activator="parent" location="top">
                                  Son 5 dakika içinde mesaj alındı
                                </v-tooltip>
                              </div>

                              <!-- Genel Status -->
                              <div class="mb-2">
                                <v-chip :color="getDataTimeBasedStatus(group.status, group.messages) === 'online' ? 'success' : 'error'" size="small">
                                  {{ getDataTimeBasedStatus(group.status, group.messages).toUpperCase() }}
                                </v-chip>
                                <span v-if="group.status.lastSeen" class="text-caption ml-2">
                                  Son Aktivite: {{ formatDate(group.status.lastSeen) }}
                                </span>
                                <!-- Veri zamanı bilgisi -->
                                <div v-if="getLatestDataTime(group.messages)" class="text-caption mt-1">
                                  <strong>Son Veri Zamanı:</strong> {{ formatUnixTime(getLatestDataTime(group.messages)) }}
                                  <v-chip v-if="isDataTimeOld(getLatestDataTime(group.messages))" color="warning" size="x-small" class="ml-1">
                                    Eski Veri
                                  </v-chip>
                                </div>
                              </div>

                              <!-- Mesaj Detayları -->
                              <v-row class="mt-2">
                                <!-- Realtime Mesajı -->
                                <v-col cols="4">
                                  <v-card variant="tonal"
                                    :color="getMessageStatusByDataTime(group.messages?.realtime) === 'active' ? 'success' : 'grey'">
                                    <v-card-title class="text-subtitle-2 pa-2">
                                      <v-icon left size="small">mdi-clock-fast</v-icon>
                                      Realtime
                                      <v-spacer></v-spacer>
                                      <v-icon v-if="group.messages?.realtime && isRecentMessage(group.messages.realtime.lastSeen)" 
                                              color="success" size="small">
                                        mdi-pulse
                                      </v-icon>
                                    </v-card-title>
                                    <v-card-text class="pa-2 pt-0">
                                      <div v-if="group.messages && group.messages.realtime">
                                        <div class="text-caption mb-1">
                                          <strong>Son Mesaj:</strong><br>
                                          {{ formatDate(group.messages.realtime.lastSeen) }}
                                        </div>
                                        <div class="text-caption mb-1">
                                          <strong>Veri Zamanı:</strong><br>
                                          {{ formatUnixTime(group.messages.realtime.unixTime) }}
                                          <v-chip v-if="isDataTimeOld(group.messages.realtime.unixTime)" color="warning" size="x-small" class="ml-1">
                                            {{ getDataTimeAge(group.messages.realtime.unixTime) }}
                                          </v-chip>
                                        </div>
                                        <v-chip :color="getMessageStatusByDataTime(group.messages.realtime) === 'active' ? 'success' : 'warning'"
                                          size="x-small" class="mt-1">
                                          {{ getMessageStatusByDataTime(group.messages.realtime) === 'active' ? 'Aktif' : 'Pasif' }}
                                        </v-chip>
                                      </div>
                                      <div v-else class="text-caption text-grey">
                                        Henüz realtime mesajı alınmadı
                                      </div>
                                    </v-card-text>
                                  </v-card>
                                </v-col>

                                <!-- Save Mesajı -->
                                <v-col cols="4">
                                  <v-card variant="tonal"
                                    :color="getMessageStatusByDataTime(group.messages?.save) === 'active' ? 'success' : 'grey'">
                                    <v-card-title class="text-subtitle-2 pa-2">
                                      <v-icon left size="small">mdi-content-save</v-icon>
                                      Save
                                      <v-spacer></v-spacer>
                                      <v-icon v-if="group.messages?.save && isRecentMessage(group.messages.save.lastSeen)" 
                                              color="success" size="small">
                                        mdi-pulse
                                      </v-icon>
                                    </v-card-title>
                                    <v-card-text class="pa-2 pt-0">
                                      <div v-if="group.messages && group.messages.save">
                                        <div class="text-caption mb-1">
                                          <strong>Son Mesaj:</strong><br>
                                          {{ formatDate(group.messages.save.lastSeen) }}
                                        </div>
                                        <div class="text-caption mb-1">
                                          <strong>Veri Zamanı:</strong><br>
                                          {{ formatUnixTime(group.messages.save.unixTime) }}
                                          <v-chip v-if="isDataTimeOld(group.messages.save.unixTime)" color="warning" size="x-small" class="ml-1">
                                            {{ getDataTimeAge(group.messages.save.unixTime) }}
                                          </v-chip>
                                        </div>
                                        <v-chip :color="getMessageStatusByDataTime(group.messages.save) === 'active' ? 'success' : 'warning'"
                                          size="x-small" class="mt-1">
                                          {{ getMessageStatusByDataTime(group.messages.save) === 'active' ? 'Aktif' : 'Pasif' }}
                                        </v-chip>
                                      </div>
                                      <div v-else class="text-caption text-grey">
                                        Henüz save mesajı alınmadı
                                      </div>
                                    </v-card-text>
                                  </v-card>
                                </v-col>

                                <!-- Synced Mesajı -->
                                <v-col cols="4">
                                  <v-card variant="tonal"
                                    :color="getMessageStatusByDataTime(group.messages?.synced) === 'active' ? 'success' : 'grey'">
                                    <v-card-title class="text-subtitle-2 pa-2">
                                      <v-icon left size="small">mdi-sync</v-icon>
                                      Synced
                                      <v-spacer></v-spacer>
                                      <v-icon v-if="group.messages?.synced && isRecentMessage(group.messages.synced.lastSeen)" 
                                              color="success" size="small">
                                        mdi-pulse
                                      </v-icon>
                                    </v-card-title>
                                    <v-card-text class="pa-2 pt-0">
                                      <div v-if="group.messages && group.messages.synced">
                                        <div class="text-caption mb-1">
                                          <strong>Son Mesaj:</strong><br>
                                          {{ formatDate(group.messages.synced.lastSeen) }}
                                        </div>
                                        <div class="text-caption mb-1">
                                          <strong>Veri Zamanı:</strong><br>
                                          {{ formatUnixTime(group.messages.synced.unixTime) }}
                                          <v-chip v-if="isDataTimeOld(group.messages.synced.unixTime)" color="warning" size="x-small" class="ml-1">
                                            {{ getDataTimeAge(group.messages.synced.unixTime) }}
                                          </v-chip>
                                        </div>
                                        <v-chip :color="getMessageStatusByDataTime(group.messages.synced) === 'active' ? 'success' : 'warning'"
                                          size="x-small" class="mt-1">
                                          {{ getMessageStatusByDataTime(group.messages.synced) === 'active' ? 'Aktif' : 'Pasif' }}
                                        </v-chip>
                                      </div>
                                      <div v-else class="text-caption text-grey">
                                        Henüz synced mesajı alınmadı
                                      </div>
                                    </v-card-text>
                                  </v-card>
                                </v-col>
                              </v-row>

                              <!-- Topic Bilgisi -->
                              <div v-if="group.status.topic" class="mt-2">
                                <div class="text-caption">
                                  <strong>Son Topic:</strong> {{ group.status.topic }}
                                </div>
                              </div>
                            </v-card>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- History -->
                    <v-col cols="12" md="6" v-if="item.history.length > 0">
                      <v-card variant="outlined">
                        <v-card-title class="text-h6">
                          <v-icon left>mdi-history</v-icon>
                          History ({{ item.history.length }})
                        </v-card-title>
                        <v-card-text>
                          <div v-for="history in item.history" :key="history.id" class="mb-4">
                            <v-card variant="outlined" class="pa-3">
                              <div class="d-flex align-center mb-2">
                                <v-icon :color="getDataTimeBasedStatus(history.status, history.messages) === 'online' ? 'success' : 'error'" size="small"
                                  class="mr-2">
                                  {{ getDataTimeBasedStatus(history.status, history.messages) === 'online' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                </v-icon>
                                <span class="font-weight-medium">{{ history.name }} (ID: {{ history.id }})</span>
                                <!-- Güncel mesaj ikonu -->
                                <v-icon v-if="isRecentMessage(history.status.lastSeen)" color="success" size="small" class="ml-2">
                                  mdi-pulse
                                </v-icon>
                                <v-tooltip v-if="isRecentMessage(history.status.lastSeen)" activator="parent" location="top">
                                  Son 5 dakika içinde mesaj alındı
                                </v-tooltip>
                              </div>

                              <!-- Genel Status -->
                              <div class="mb-2">
                                <v-chip :color="getDataTimeBasedStatus(history.status, history.messages) === 'online' ? 'success' : 'error'" size="small">
                                  {{ getDataTimeBasedStatus(history.status, history.messages).toUpperCase() }}
                                </v-chip>
                                <span v-if="history.status.lastSeen" class="text-caption ml-2">
                                  Son Aktivite: {{ formatDate(history.status.lastSeen) }}
                                </span>
                                <div class="text-caption mt-1">
                                  Kayıt Aralığı: {{ history.saveInterval }}s
                                </div>
                                <!-- Veri zamanı bilgisi -->
                                <div v-if="getLatestDataTime(history.messages)" class="text-caption mt-1">
                                  <strong>Son Veri Zamanı:</strong> {{ formatUnixTime(getLatestDataTime(history.messages)) }}
                                  <v-chip v-if="isDataTimeOld(getLatestDataTime(history.messages))" color="warning" size="x-small" class="ml-1">
                                    Eski Veri
                                  </v-chip>
                                </div>
                              </div>

                              <!-- Mesaj Detayları -->
                              <v-row class="mt-2">
                                <!-- History Mesajı -->
                                <v-col cols="6" v-if="history.messages.history">
                                  <v-card variant="tonal"
                                    :color="getMessageStatusByDataTime(history.messages?.history) === 'active' ? 'success' : 'grey'">
                                    <v-card-title class="text-subtitle-2 pa-2">
                                      <v-icon left size="small">mdi-database</v-icon>
                                      History
                                      <v-spacer></v-spacer>
                                      <v-icon v-if="history.messages?.history && isRecentMessage(history.messages.history.lastSeen)" 
                                              color="success" size="small">
                                        mdi-pulse
                                      </v-icon>
                                    </v-card-title>
                                    <v-card-text class="pa-2 pt-0">
                                      <div v-if="history.messages && history.messages.history">
                                        <div class="text-caption mb-1">
                                          <strong>Son Mesaj:</strong><br>
                                          {{ formatDate(history.messages.history.lastSeen) }}
                                        </div>
                                        <div class="text-caption mb-1">
                                          <strong>Veri Zamanı:</strong><br>
                                          {{ formatUnixTime(history.messages.history.unixTime) }}
                                          <v-chip v-if="isDataTimeOld(history.messages.history.unixTime)" color="warning" size="x-small" class="ml-1">
                                            {{ getDataTimeAge(history.messages.history.unixTime) }}
                                          </v-chip>
                                        </div>
                                        <v-chip :color="getMessageStatusByDataTime(history.messages.history) === 'active' ? 'success' : 'warning'"
                                          size="x-small" class="mt-1">
                                          {{ getMessageStatusByDataTime(history.messages.history) === 'active' ? 'Aktif' : 'Pasif' }}
                                        </v-chip>
                                      </div>
                                      <div v-else class="text-caption text-grey">
                                        Henüz history mesajı alınmadı
                                      </div>
                                    </v-card-text>
                                  </v-card>
                                </v-col>

                                <!-- History-Multi Mesajı -->
                                <v-col cols="6" v-if="history.messages['history-multi']">
                                  <v-card variant="tonal"
                                    :color="getMessageStatusByDataTime(history.messages?.['history-multi']) === 'active' ? 'success' : 'grey'">
                                    <v-card-title class="text-subtitle-2 pa-2">
                                      <v-icon left size="small">mdi-database-plus</v-icon>
                                      History-Multi
                                      <v-spacer></v-spacer>
                                      <v-icon v-if="history.messages?.['history-multi'] && isRecentMessage(history.messages['history-multi'].lastSeen)" 
                                              color="success" size="small">
                                        mdi-pulse
                                      </v-icon>
                                    </v-card-title>
                                    <v-card-text class="pa-2 pt-0">
                                      <div v-if="history.messages && history.messages['history-multi']">
                                        <div class="text-caption mb-1">
                                          <strong>Son Mesaj:</strong><br>
                                          {{ formatDate(history.messages['history-multi'].lastSeen) }}
                                        </div>
                                        <div class="text-caption mb-1">
                                          <strong>Veri Zamanı:</strong><br>
                                          {{ formatUnixTime(history.messages['history-multi'].unixTime) }}
                                          <v-chip v-if="isDataTimeOld(history.messages['history-multi'].unixTime)" color="warning" size="x-small" class="ml-1">
                                            {{ getDataTimeAge(history.messages['history-multi'].unixTime) }}
                                          </v-chip>
                                        </div>
                                        <v-chip
                                          :color="getMessageStatusByDataTime(history.messages['history-multi']) === 'active' ? 'success' : 'warning'"
                                          size="x-small" class="mt-1">
                                          {{ getMessageStatusByDataTime(history.messages['history-multi']) === 'active' ? 'Aktif' : 'Pasif' }}
                                        </v-chip>
                                      </div>
                                      <div v-else class="text-caption text-grey">
                                        Henüz history-multi mesajı alınmadı
                                      </div>
                                    </v-card-text>
                                  </v-card>
                                </v-col>

                                <!-- History-Synced Mesajı -->
                                <v-col cols="6" v-if="history.messages['history-synced']">
                                  <v-card variant="tonal"
                                    :color="getMessageStatusByDataTime(history.messages?.['history-synced']) === 'active' ? 'success' : 'grey'">
                                    <v-card-title class="text-subtitle-2 pa-2">
                                      <v-icon left size="small">mdi-sync</v-icon>
                                      History-Synced
                                      <v-spacer></v-spacer>
                                      <v-icon v-if="history.messages?.['history-synced'] && isRecentMessage(history.messages['history-synced'].lastSeen)" 
                                              color="success" size="small">
                                        mdi-pulse
                                      </v-icon>
                                    </v-card-title>
                                    <v-card-text class="pa-2 pt-0">
                                      <div v-if="history.messages && history.messages['history-synced']">
                                        <div class="text-caption mb-1">
                                          <strong>Son Mesaj:</strong><br>
                                          {{ formatDate(history.messages['history-synced'].lastSeen) }}
                                        </div>
                                        <div class="text-caption mb-1">
                                          <strong>Veri Zamanı:</strong><br>
                                          {{ formatUnixTime(history.messages['history-synced'].unixTime) }}
                                          <v-chip v-if="isDataTimeOld(history.messages['history-synced'].unixTime)" color="warning" size="x-small" class="ml-1">
                                            {{ getDataTimeAge(history.messages['history-synced'].unixTime) }}
                                          </v-chip>
                                        </div>
                                        <v-chip
                                          :color="getMessageStatusByDataTime(history.messages['history-synced']) === 'active' ? 'success' : 'warning'"
                                          size="x-small" class="mt-1">
                                          {{ getMessageStatusByDataTime(history.messages['history-synced']) === 'active' ? 'Aktif' : 'Pasif' }}
                                        </v-chip>
                                      </div>
                                      <div v-else class="text-caption text-grey">
                                        Henüz history-synced mesajı alınmadı
                                      </div>
                                    </v-card-text>
                                  </v-card>
                                </v-col>

                                <!-- History-Multi-Synced Mesajı -->
                                <v-col cols="6" v-if="history.messages['history-multi-synced']">
                                  <v-card variant="tonal"
                                    :color="getMessageStatusByDataTime(history.messages?.['history-multi-synced']) === 'active' ? 'success' : 'grey'">
                                    <v-card-title class="text-subtitle-2 pa-2">
                                      <v-icon left size="small">mdi-sync-circle</v-icon>
                                      History-Multi-Synced
                                      <v-spacer></v-spacer>
                                      <v-icon v-if="history.messages?.['history-multi-synced'] && isRecentMessage(history.messages['history-multi-synced'].lastSeen)" 
                                              color="success" size="small">
                                        mdi-pulse
                                      </v-icon>
                                    </v-card-title>
                                    <v-card-text class="pa-2 pt-0">
                                      <div v-if="history.messages && history.messages['history-multi-synced']">
                                        <div class="text-caption mb-1">
                                          <strong>Son Mesaj:</strong><br>
                                          {{ formatDate(history.messages['history-multi-synced'].lastSeen) }}
                                        </div>
                                        <div class="text-caption mb-1">
                                          <strong>Veri Zamanı:</strong><br>
                                          {{ formatUnixTime(history.messages['history-multi-synced'].unixTime) }}
                                          <v-chip v-if="isDataTimeOld(history.messages['history-multi-synced'].unixTime)" color="warning" size="x-small" class="ml-1">
                                            {{ getDataTimeAge(history.messages['history-multi-synced'].unixTime) }}
                                          </v-chip>
                                        </div>
                                        <v-chip
                                          :color="getMessageStatusByDataTime(history.messages['history-multi-synced']) === 'active' ? 'success' : 'warning'"
                                          size="x-small" class="mt-1">
                                          {{ getMessageStatusByDataTime(history.messages['history-multi-synced']) === 'active' ? 'Aktif' : 'Pasif' }}
                                        </v-chip>
                                      </div>
                                      <div v-else class="text-caption text-grey">
                                        Henüz history-multi-synced mesajı alınmadı
                                      </div>
                                    </v-card-text>
                                  </v-card>
                                </v-col>
                              </v-row>

                              <!-- Topic Bilgisi -->
                              <div v-if="history.status.topic" class="mt-2">
                                <div class="text-caption">
                                  <strong>Son Topic:</strong> {{ history.status.topic }}
                                </div>
                              </div>
                            </v-card>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Clients -->
                    <v-col cols="6" v-if="item.clients.length > 0">
                      <v-card variant="outlined">
                        <v-card-title class="text-h6">
                          <v-icon left>mdi-desktop-classic</v-icon>
                          Clients ({{ item.clients.length }})
                        </v-card-title>
                        <v-card-text>
                          <div v-for="client in item.clients" :key="client.id" class="mb-6">
                            <v-card variant="outlined" class="pa-3">
                              <div class="d-flex align-center mb-2">
                                <v-icon :color="getClientDataTimeBasedStatus(client) === 'online' ? 'success' : 'error'" size="small"
                                  class="mr-2">
                                  {{ getClientDataTimeBasedStatus(client) === 'online' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                </v-icon>
                                <span class="font-weight-medium">{{ client.mqttClient }}</span>
                                <!-- Güncel mesaj ikonu -->
                                <v-icon v-if="isRecentMessage(client.status.lastSeen)" color="success" size="small" class="ml-2">
                                  mdi-pulse
                                </v-icon>
                                <v-tooltip v-if="isRecentMessage(client.status.lastSeen)" activator="parent" location="top">
                                  Son 5 dakika içinde mesaj alındı
                                </v-tooltip>
                              </div>

                              <!-- Client Bilgileri -->
                              <div class="mb-3">
                                <v-chip :color="getClientDataTimeBasedStatus(client) === 'online' ? 'success' : 'error'" size="small">
                                  {{ getClientDataTimeBasedStatus(client).toUpperCase() }}
                                </v-chip>
                                <span v-if="client.status.lastSeen" class="text-caption ml-2">
                                  Son Aktivite: {{ formatDate(client.status.lastSeen) }}
                                </span>
                                <div class="text-caption mt-1">
                                  {{ client.manufacturer }} {{ client.model }}
                                </div>
                                <!-- Son Restart Zamanı -->
                                <div v-if="client.restartTime" class="text-caption mt-1">
                                  <v-icon size="x-small" color="warning" class="mr-1">mdi-restart</v-icon>
                                  <strong>Son Restart:</strong> {{ formatDate(client.restartTime.restartTime) }}
                                </div>
                              </div>

                              <!-- Son Group Mesajı -->
                              <div v-if="client.groupMessages" class="mb-4">
                                <h4 class="text-subtitle-2 mb-2">
                                  <v-icon left size="small">mdi-folder-multiple</v-icon>
                                  Son Group Mesajı
                                </h4>
                                <v-card variant="outlined" class="pa-3">
                                  <div class="text-body-2 font-weight-medium mb-2">
                                    {{ client.groupMessages.groupName }} (ID: {{ client.groupMessages.groupId }})
                                  </div>
                                  <v-row>
                                    <v-col cols="12">
                                      <v-card variant="tonal"
                                        :color="getMessageStatusByDataTime(client.groupMessages) === 'active' ? 'success' : 'grey'" class="pa-2">
                                        <div class="text-caption font-weight-medium mb-1">
                                          <v-icon left size="x-small">
                                            {{ client.groupMessages.messageType === 'realtime' ? 'mdi-clock-fast' :
                                              client.groupMessages.messageType === 'save' ? 'mdi-content-save' :
                                                'mdi-sync' }}
                                          </v-icon>
                                          {{ client.groupMessages.messageType === 'realtime' ? 'Realtime' :
                                            client.groupMessages.messageType === 'save' ? 'Save' : 'Synced' }}
                                        </div>
                                        <div class="text-caption">
                                          <div><strong>Son Mesaj:</strong> {{ formatDate(client.groupMessages.lastSeen)
                                          }}</div>
                                          <div><strong>Veri Zamanı:</strong> {{
                                            formatUnixTime(client.groupMessages.unixTime) }}
                                            <v-chip v-if="isDataTimeOld(client.groupMessages.unixTime)" color="warning" size="x-small" class="ml-1">
                                              {{ getDataTimeAge(client.groupMessages.unixTime) }}
                                            </v-chip>
                                          </div>
                                          <v-chip :color="getMessageStatusByDataTime(client.groupMessages) === 'active' ? 'success' : 'warning'"
                                            size="x-small" class="mt-1">
                                            {{ getMessageStatusByDataTime(client.groupMessages) === 'active' ? 'Aktif' : 'Pasif' }}
                                          </v-chip>
                                        </div>
                                      </v-card>
                                    </v-col>
                                  </v-row>
                                </v-card>
                              </div>

                              <!-- Son History Mesajı -->
                              <div v-if="client.historyMessages" class="mb-4">
                                <h4 class="text-subtitle-2 mb-2">
                                  <v-icon left size="small">mdi-history</v-icon>
                                  Son History Mesajı
                                </h4>
                                <v-card variant="outlined" class="pa-3">
                                  <div class="text-body-2 font-weight-medium mb-2">
                                    {{ client.historyMessages.historyName }} (ID: {{ client.historyMessages.historyId
                                    }})
                                  </div>
                                  <v-row>
                                    <v-col cols="12">
                                      <v-card variant="tonal"
                                        :color="getMessageStatusByDataTime(client.historyMessages) === 'active' ? 'success' : 'grey'" class="pa-2">
                                        <div class="text-caption font-weight-medium mb-1">
                                          <v-icon left size="x-small">
                                            {{ client.historyMessages.messageType === 'history' ? 'mdi-database' :
                                              client.historyMessages.messageType === 'history-multi' ? 'mdi-database-plus'
                                                :
                                                client.historyMessages.messageType === 'history-synced' ? 'mdi-sync' :
                                            'mdi-sync-circle' }}
                                          </v-icon>
                                          {{ client.historyMessages.messageType === 'history' ? 'History' :
                                            client.historyMessages.messageType === 'history-multi' ? 'History-Multi' :
                                              client.historyMessages.messageType === 'history-synced' ? 'History-Synced' :
                                                'History-Multi-Synced' }}
                                        </div>
                                        <div class="text-caption">
                                          <div><strong>Son Mesaj:</strong> {{
                                            formatDate(client.historyMessages.lastSeen) }}</div>
                                          <div><strong>Veri Zamanı:</strong> {{
                                            formatUnixTime(client.historyMessages.unixTime) }}
                                            <v-chip v-if="isDataTimeOld(client.historyMessages.unixTime)" color="warning" size="x-small" class="ml-1">
                                              {{ getDataTimeAge(client.historyMessages.unixTime) }}
                                            </v-chip>
                                          </div>
                                          <v-chip :color="getMessageStatusByDataTime(client.historyMessages) === 'active' ? 'success' : 'warning'"
                                            size="x-small" class="mt-1">
                                            {{ getMessageStatusByDataTime(client.historyMessages) === 'active' ? 'Aktif' : 'Pasif' }}
                                          </v-chip>
                                        </div>
                                      </v-card>
                                    </v-col>
                                  </v-row>
                                </v-card>
                              </div>

                              <!-- Topic Bilgisi -->
                              <div v-if="client.status.topic" class="mt-2">
                                <div class="text-caption">
                                  <strong>Son Topic:</strong> {{ client.status.topic }}
                                </div>
                              </div>

                              <!-- Restart Button -->
                              <div class="mt-3">
                                <v-btn color="warning" size="small" @click="showRestartDialog(client)"
                                  :loading="client.restarting" class="mr-2">
                                  <v-icon left size="small">mdi-restart</v-icon>
                                  Restart
                                </v-btn>
                                <v-btn color="info" size="small" @click="showAutoRestartDialog(client)">
                                  <v-icon left size="small">mdi-timer-cog</v-icon>
                                  Otomatik Restart
                                </v-btn>
                              </div>

                              <!-- Restart Response Message -->
                              <div v-if="client.restartMessage" class="mt-2">
                                <v-alert
                                  :type="client.restartMessage.success === null ? 'info' : (client.restartMessage.success ? 'success' : 'error')"
                                  density="compact" closable @click:close="client.restartMessage = null">
                                  {{ client.restartMessage.message }}
                                </v-alert>
                              </div>
                            </v-card>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>


                  </v-row>
                </v-container>
              </td>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Restart Confirmation Dialog -->
    <v-dialog v-model="restartDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="warning">mdi-restart</v-icon>
          Client Restart
        </v-card-title>
        <v-card-text>
          <p><strong>{{ selectedClient?.mqttClient }}</strong> client'ını yeniden başlatmak istediğinizden emin misiniz?
          </p>
          <p class="text-caption text-grey">Bu işlem client'ın yeniden başlatılmasına neden olacaktır.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="restartDialog = false">İptal</v-btn>
          <v-btn color="warning" @click="confirmRestart" :loading="restartingClient">
            Restart
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Auto Restart Settings Dialog -->
    <v-dialog v-model="autoRestartDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="info">mdi-timer-cog</v-icon>
          Otomatik Restart Ayarları
        </v-card-title>
        <v-card-subtitle>
          {{ selectedClient?.mqttClient }} - {{ selectedClient?.manufacturer }} {{ selectedClient?.model }}
        </v-card-subtitle>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-switch v-model="autoRestartSettings.is_active" label="Otomatik Restart Aktif" color="primary"
                hide-details></v-switch>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div class="text-subtitle-2 mb-3">Timeout Ayarları (Dakika)</div>
          <p class="text-caption text-grey mb-4">
            Belirtilen süre boyunca mesaj gelmezse otomatik restart komutu gönderilir. 0 değeri o mesaj tipi için
            restart'ı
            devre dışı bırakır.
          </p>

          <v-row>
            <v-col cols="6">
              <v-text-field v-model.number="autoRestartSettings.save_timeout" label="Save Mesajı Timeout" type="number"
                min="0" suffix="dakika" variant="outlined" density="compact"
                :disabled="!autoRestartSettings.is_active"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model.number="autoRestartSettings.realtime_timeout" label="Realtime Mesajı Timeout"
                type="number" min="0" suffix="dakika" variant="outlined" density="compact"
                :disabled="!autoRestartSettings.is_active"></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-text-field v-model.number="autoRestartSettings.history_timeout" label="History Mesajı Timeout"
                type="number" min="0" suffix="dakika" variant="outlined" density="compact"
                :disabled="!autoRestartSettings.is_active"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model.number="autoRestartSettings.history_multi_timeout"
                label="History-Multi Mesajı Timeout" type="number" min="0" suffix="dakika" variant="outlined"
                density="compact" :disabled="!autoRestartSettings.is_active"></v-text-field>
            </v-col>
          </v-row>

          <v-alert type="info" density="compact" class="mt-4">
            <v-icon left>mdi-information</v-icon>
            Sistem her dakika kontrol yapar ve belirlenen süre aşıldığında otomatik restart komutu gönderir.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="autoRestartDialog = false">İptal</v-btn>
          <v-btn color="primary" @click="saveAutoRestartSettings" :loading="savingAutoRestart">
            Kaydet
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// axios artık $api olarak global olarak mevcut

export default {
  name: 'Dashboard',
  data() {
    return {
      dataloggers: [],
      loading: false,
      restartDialog: false,
      autoRestartDialog: false,
      selectedClient: null,
      restartingClient: false,
      mqttClient: null,
      autoRestartSettings: {
        save_timeout: 0,
        realtime_timeout: 0,
        history_timeout: 0,
        history_multi_timeout: 0,
        is_active: true
      },
      savingAutoRestart: false,
      headers: [
        { title: 'Datalogger', key: 'name', sortable: true },
        { title: 'Server', key: 'server', sortable: true, align: 'center' },
        { title: 'Groups', key: 'groups', sortable: false, align: 'center' },
        { title: 'History', key: 'history', sortable: false, align: 'center' },
        { title: 'Clients', key: 'clients', sortable: false, align: 'center' },
        { title: 'Offline Oranı', key: 'offlineRatio', sortable: true, align: 'center' }
      ],
      // Veri zamanı threshold'ları (dakika)
      dataTimeThresholds: {
        realtime: 5,    // 5 dakika
        save: 60,       // 60 dakika  
        synced: 120,    // 120 dakika
        history: 60,    // 60 dakika
        'history-multi': 60,  // 60 dakika
        'history-synced': 120, // 120 dakika
        'history-multi-synced': 120 // 120 dakika
      }
    }
  },
  computed: {
    sortedDataloggers() {
      return [...this.dataloggers].sort((a, b) => {
        const aOfflineRatio = this.calculateOfflineRatio(a)
        const bOfflineRatio = this.calculateOfflineRatio(b)
        // Offline oranı yüksek olanlar üste gelsin (büyükten küçüğe)
        return bOfflineRatio - aOfflineRatio
      })
    }
  },
  mounted() {
    this.loadData()
    // Her 30 saniyede bir otomatik yenile
    this.interval = setInterval(this.loadData, 30000)
  },
  beforeUnmount() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  },
  methods: { 
   async loadData() {
      this.loading = true
      try {
        const response = await this.$api.get('/api/status')
        this.dataloggers = response.data
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        this.loading = false
      }
    },

    // Veri zamanına göre mesaj durumunu kontrol et
    getMessageStatusByDataTime(message) {
      if (!message || !message.unixTime) {
        return 'inactive'
      }

      const now = new Date()
      const dataTime = new Date(message.unixTime * 1000)
      const minutesDiff = Math.floor((now - dataTime) / (1000 * 60))

      // Mesaj tipine göre threshold belirle
      let threshold = 60 // default 60 dakika
      if (message.messageType) {
        threshold = this.dataTimeThresholds[message.messageType] || 60
      }

      return minutesDiff <= threshold ? 'active' : 'inactive'
    },

    // Veri zamanının eski olup olmadığını kontrol et
    isDataTimeOld(unixTime) {
      if (!unixTime) return false
      
      const now = new Date()
      const dataTime = new Date(unixTime * 1000)
      const minutesDiff = Math.floor((now - dataTime) / (1000 * 60))
      
      return minutesDiff > 60 // 60 dakikadan eski ise eski kabul et
    },

    // Mesajın son 5 dakika içinde gelip gelmediğini kontrol et
    isRecentMessage(lastSeen) {
      if (!lastSeen) return false
      
      const now = new Date()
      const messageTime = new Date(lastSeen)
      const minutesDiff = Math.floor((now - messageTime) / (1000 * 60))
      
      return minutesDiff <= 5 // Son 5 dakika içinde geldiyse güncel
    },

    // Datalogger'ın herhangi bir component'inde son 5 dakika içinde mesaj var mı kontrol et
    hasRecentMessage(datalogger) {
      // Groups kontrol et
      for (const group of datalogger.groups) {
        if (this.isRecentMessage(group.status.lastSeen)) return true
      }
      
      // History kontrol et
      for (const history of datalogger.history) {
        if (this.isRecentMessage(history.status.lastSeen)) return true
      }
      
      // Clients kontrol et
      for (const client of datalogger.clients) {
        if (this.isRecentMessage(client.status.lastSeen)) return true
      }
      
      return false
    },

    // Veri zamanının ne kadar eski olduğunu göster
    getDataTimeAge(unixTime) {
      if (!unixTime) return ''
      
      const now = new Date()
      const dataTime = new Date(unixTime * 1000)
      const minutesDiff = Math.floor((now - dataTime) / (1000 * 60))
      
      if (minutesDiff < 60) {
        return `${minutesDiff}dk`
      } else if (minutesDiff < 1440) {
        return `${Math.floor(minutesDiff / 60)}sa`
      } else {
        return `${Math.floor(minutesDiff / 1440)}gün`
      }
    },

    // En son veri zamanını bul
    getLatestDataTime(messages) {
      if (!messages) return null
      
      let latestTime = 0
      Object.values(messages).forEach(message => {
        if (message && message.unixTime && message.unixTime > latestTime) {
          latestTime = message.unixTime
        }
      })
      
      return latestTime > 0 ? latestTime : null
    },

    // Veri zamanına göre genel status belirle
    getDataTimeBasedStatus(status, messages) {
      if (!messages) return 'offline'
      
      // Herhangi bir mesaj aktif mi kontrol et
      const messageTypes = Object.keys(messages)
      for (const messageType of messageTypes) {
        const message = messages[messageType]
        if (message && this.getMessageStatusByDataTime(message) === 'active') {
          return 'online'
        }
      }
      
      return 'offline'
    },

    // Client için veri zamanına göre status
    getClientDataTimeBasedStatus(client) {
      let hasActiveMessage = false
      
      // Group mesajlarını kontrol et
      if (client.groupMessages && this.getMessageStatusByDataTime(client.groupMessages) === 'active') {
        hasActiveMessage = true
      }
      
      // History mesajlarını kontrol et
      if (client.historyMessages && this.getMessageStatusByDataTime(client.historyMessages) === 'active') {
        hasActiveMessage = true
      }
      
      return hasActiveMessage ? 'online' : 'offline'
    },

    getDataloggerStatus(datalogger) {
      const onlineGroups = datalogger.groups.filter(g => this.getDataTimeBasedStatus(g.status, g.messages) === 'online').length
      const totalGroups = datalogger.groups.length

      const onlineHistory = datalogger.history.filter(h => this.getDataTimeBasedStatus(h.status, h.messages) === 'online').length
      const totalHistory = datalogger.history.length

      const onlineClients = datalogger.clients.filter(c => this.getClientDataTimeBasedStatus(c) === 'online').length
      const totalClients = datalogger.clients.length

      const totalOnline = onlineGroups + onlineHistory + onlineClients
      const totalItems = totalGroups + totalHistory + totalClients

      if (totalItems === 0) return 'No Data'
      if (totalOnline === 0) return 'Offline'
      if (totalOnline === totalItems) return 'Online'
      return `Partial (${totalOnline}/${totalItems})`
    },

    getDataloggerStatusColor(datalogger) {
      const onlineGroups = datalogger.groups.filter(g => this.getDataTimeBasedStatus(g.status, g.messages) === 'online').length
      const totalGroups = datalogger.groups.length

      const onlineHistory = datalogger.history.filter(h => this.getDataTimeBasedStatus(h.status, h.messages) === 'online').length
      const totalHistory = datalogger.history.length

      const onlineClients = datalogger.clients.filter(c => this.getClientDataTimeBasedStatus(c) === 'online').length
      const totalClients = datalogger.clients.length

      const totalOnline = onlineGroups + onlineHistory + onlineClients
      const totalItems = totalGroups + totalHistory + totalClients

      if (totalItems === 0) return 'grey'
      if (totalOnline === 0) return 'error'
      if (totalOnline === totalItems) return 'success'
      return 'warning'
    },

    getOnlineCount(items) {
      return items.filter(item => {
        if (item.messages) {
          return this.getDataTimeBasedStatus(item.status, item.messages) === 'online'
        } else {
          return this.getClientDataTimeBasedStatus(item) === 'online'
        }
      }).length
    },

    getStatusText(items) {
      if (!items || items.length === 0) {
        return '-'
      }
      const onlineCount = this.getOnlineCount(items)
      return `${onlineCount}/${items.length}`
    },

    getStatusColor(items) {
      if (!items || items.length === 0) {
        return 'grey'
      }
      const onlineCount = this.getOnlineCount(items)
      if (onlineCount === 0) return 'error'
      if (onlineCount === items.length) return 'success'
      return 'warning'
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleString('tr-TR')
    },

    formatUnixTime(unixTime) {
      if (!unixTime) return '-'
      // Unix timestamp'i milisaniyeye çevir ve tarih formatına dönüştür
      return new Date(unixTime * 1000).toLocaleString('tr-TR')
    },

    showRestartDialog(client) {
      this.selectedClient = client
      this.restartDialog = true
    },

    async confirmRestart() {
      if (!this.selectedClient) return

      this.restartingClient = true

      try {
        // Random UUID oluştur
        const requestId = this.generateUUID()

        // MQTT mesajını gönder
        const restartMessage = {
          type: "restart",
          user: {
            id: 0,
            name: "Logger Status"
          },
          request_id: requestId
        }

        // Backend'e restart isteği gönder
        await this.$api.post('/api/status/restart-client', {
          clientUuid: this.selectedClient.uuid,
          message: restartMessage
        })

        // Modal'ı kapat ve başarı mesajı göster
        this.restartDialog = false
        this.selectedClient.restartMessage = {
          success: true,
          message: 'Restart komutu başarıyla gönderildi'
        }

        // 1 saniye sonra status'u tekrar çek
        setTimeout(() => {
          this.loadData()
        }, 1000)

      } catch (error) {
        console.error('Restart error:', error)
        this.selectedClient.restartMessage = {
          success: false,
          message: 'Restart isteği gönderilirken hata oluştu: ' + error.message
        }
        this.restartDialog = false
      } finally {
        this.restartingClient = false
      }
    },

    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0
        const v = c == 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
      }).replace(/-/g, '')
    },

    async showAutoRestartDialog(client) {
      this.selectedClient = client

      try {
        // Mevcut auto restart ayarlarını getir
        const response = await this.$api.get(`/api/servers/client/${client.id}/auto-restart`)
        this.autoRestartSettings = { ...response.data }
      } catch (error) {
        console.error('Error loading auto restart settings:', error)
        // Hata durumunda default değerleri kullan
        this.autoRestartSettings = {
          save_timeout: 0,
          realtime_timeout: 0,
          history_timeout: 0,
          history_multi_timeout: 0,
          is_active: true
        }
      }

      this.autoRestartDialog = true
    },

    async saveAutoRestartSettings() {
      if (!this.selectedClient) return

      this.savingAutoRestart = true

      try {
        await this.$api.put(`/api/servers/client/${this.selectedClient.id}/auto-restart`, {
          save_timeout: this.autoRestartSettings.save_timeout || 0,
          realtime_timeout: this.autoRestartSettings.realtime_timeout || 0,
          history_timeout: this.autoRestartSettings.history_timeout || 0,
          history_multi_timeout: this.autoRestartSettings.history_multi_timeout || 0,
          is_active: this.autoRestartSettings.is_active
        })

        this.autoRestartDialog = false

        // Başarı mesajı göster
        this.selectedClient.restartMessage = {
          success: true,
          message: 'Otomatik restart ayarları başarıyla kaydedildi'
        }

        // 3 saniye sonra mesajı temizle
        setTimeout(() => {
          if (this.selectedClient.restartMessage) {
            this.selectedClient.restartMessage = null
          }
        }, 3000)

      } catch (error) {
        console.error('Error saving auto restart settings:', error)
        this.selectedClient.restartMessage = {
          success: false,
          message: 'Ayarlar kaydedilirken hata oluştu: ' + error.message
        }
      } finally {
        this.savingAutoRestart = false
      }
    },

    calculateOfflineRatio(datalogger) {
      const onlineGroups = datalogger.groups.filter(g => this.getDataTimeBasedStatus(g.status, g.messages) === 'online').length
      const totalGroups = datalogger.groups.length

      const onlineHistory = datalogger.history.filter(h => this.getDataTimeBasedStatus(h.status, h.messages) === 'online').length
      const totalHistory = datalogger.history.length

      const onlineClients = datalogger.clients.filter(c => this.getClientDataTimeBasedStatus(c) === 'online').length
      const totalClients = datalogger.clients.length

      const totalOnline = onlineGroups + onlineHistory + onlineClients
      const totalItems = totalGroups + totalHistory + totalClients

      if (totalItems === 0) return 0

      const offlineItems = totalItems - totalOnline
      return offlineItems / totalItems
    },

    getOfflineRatioColor(ratio) {
      if (ratio === 0) return 'success'
      if (ratio < 0.3) return 'warning'
      if (ratio < 0.7) return 'orange'
      return 'error'
    }

  }
}
</script>